"use client";

import { c as _c } from "react/compiler-runtime";
import { AlignJustify, ChevronDown, X } from "lucide-react";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { ButtonRadialFill } from "@/components/button-radial-fill";
import MenuDropdown from "@/components/menu-dropdown";
import { usePageTransition } from "@/components/page-transition-provider";
import { TransitionLink } from "@/components/transition-link";
import { Drawer, DrawerClose, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from "@/components/ui/drawer";
import { type NavItem, servicesNavItem } from "@/layouts/nav-items";
import { scrollToSection } from "@/lib/scroll-to-section";
import { cn } from "@/lib/utils";
import Logo from "~/public/assets/logo-flow.png";

/**
 * Exceção da LP FlowFoundation: o CTA do header rola até o formulário da
 * própria página em vez de navegar para /solicitar-proposta.
 */
const flowFoundationPath = "/servicos/flowfoundation";
const flowFoundationFormId = "flowfoundation-form";
const navItems: NavItem[] = [{
  label: "Início",
  href: "/"
}, servicesNavItem, {
  label: "Cases",
  href: "/portfolio"
}, {
  label: "Sobre",
  href: "/sobre"
}, {
  label: "Blog",
  href: "/blog"
}, {
  label: "Contato",
  href: "/contato"
}];
export function Header() {
  const $ = _c(81);
  const {
    navigate
  } = usePageTransition();
  const [isCompact, setIsCompact] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [openPopover, setOpenPopover] = useState(null);
  const [currentHash, setCurrentHash] = useState("");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [servicesNavItem.groups?.[0]?.label ?? ""];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [openMobileGroups, setOpenMobileGroups] = useState(t0);
  const pathname = usePathname();
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = label => {
      setOpenMobileGroups(current => current.includes(label) ? current.filter(item => item !== label) : [...current, label]);
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const toggleMobileGroup = t1;
  let t2;
  if ($[2] !== navigate || $[3] !== pathname) {
    t2 = href => {
      const nextHref = href.startsWith("/#") && pathname === "/" ? href.replace("/", "") : href;
      navigate(nextHref);
      setIsDrawerOpen(false);
    };
    $[2] = navigate;
    $[3] = pathname;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const navigateToSection = t2;
  let t3;
  if ($[5] !== pathname) {
    t3 = href_0 => href_0.startsWith("/#") && pathname === "/" ? href_0.replace("/", "") : href_0;
    $[5] = pathname;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const resolveHref = t3;
  let t4;
  if ($[7] !== currentHash || $[8] !== pathname) {
    t4 = href_1 => {
      const [pathPart, hashPart] = href_1.split("#");
      const normalizedPath = pathPart || "/";
      if (hashPart) {
        return pathname === normalizedPath && currentHash === `#${hashPart}`;
      }
      if (normalizedPath === "/") {
        return pathname === "/" && !currentHash;
      }
      return pathname === normalizedPath;
    };
    $[7] = currentHash;
    $[8] = pathname;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const isHrefActive = t4;
  let t5;
  if ($[10] !== isHrefActive) {
    t5 = item_0 => isHrefActive(item_0.href) || item_0.groups?.some(group => group.items.some(child => isHrefActive(child.href)));
    $[10] = isHrefActive;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const isItemActive = t5;
  let t6;
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      const updateHeader = () => {
        setIsCompact(window.scrollY > 16);
      };
      updateHeader();
      window.addEventListener("scroll", updateHeader, {
        passive: true
      });
      return () => window.removeEventListener("scroll", updateHeader);
    };
    t7 = [];
    $[12] = t6;
    $[13] = t7;
  } else {
    t6 = $[12];
    t7 = $[13];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      const syncHash = () => {
        setCurrentHash(window.location.hash);
      };
      syncHash();
      window.addEventListener("hashchange", syncHash);
      return () => window.removeEventListener("hashchange", syncHash);
    };
    t9 = [];
    $[14] = t8;
    $[15] = t9;
  } else {
    t8 = $[14];
    t9 = $[15];
  }
  useEffect(t8, t9);
  const t10 = isCompact ? "pt-0" : "pt-0";
  let t11;
  if ($[16] !== t10) {
    t11 = cn("pointer-events-none fixed inset-x-0 top-0 z-200 text-white transition-all duration-500", t10);
    $[16] = t10;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  const t12 = isCompact ? "" : "px-4 sm:px-6 lg:px-10";
  let t13;
  if ($[18] !== t12) {
    t13 = cn("transition-all duration-500 ease-in-out", t12);
    $[18] = t12;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  const t14 = isCompact ? "h-17 container  border-white/10 bg-gray-1000/40 px-4 shadow-[0_18px_50px_rgba(0,0,0,0.28)] backdrop-blur-2xl md:px-8" : "h-22.5 max-w-800 border-transparent px-0";
  let t15;
  if ($[20] !== t14) {
    t15 = cn("mx-auto pointer-events-auto flex border-b border-x w-full items-center justify-between transition-all duration-500 ease-in-out", t14);
    $[20] = t14;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  const t16 = isCompact ? "max-w-30 md:max-w-32" : "max-w-36 md:max-w-40";
  let t17;
  if ($[22] !== t16) {
    t17 = cn("h-auto w-full transition-all duration-300", t16);
    $[22] = t16;
    $[23] = t17;
  } else {
    t17 = $[23];
  }
  let t18;
  if ($[24] !== t17) {
    t18 = <TransitionLink href="/" aria-label="Flowlab Tech"><Image src={Logo} alt="" aria-hidden="true" className={t17} width={2000} height={2000} loading="eager" /></TransitionLink>;
    $[24] = t17;
    $[25] = t18;
  } else {
    t18 = $[25];
  }
  let t19;
  if ($[26] !== isCompact || $[27] !== isItemActive || $[28] !== openPopover || $[29] !== resolveHref) {
    t19 = navItems.map(item_1 => <li key={item_1.label} className="relative">{item_1.groups ? <MenuDropdown item={item_1} isActive={Boolean(isItemActive(item_1))} isCompact={isCompact} openPopover={openPopover} setOpenPopover={setOpenPopover} /> : <TransitionLink href={resolveHref(item_1.href)} className={cn("rounded-2xl px-4 py-3 text-[15px] transition-colors", isItemActive(item_1) ? "bg-white/5 text-primary-300" : "text-white/80 hover:bg-white/5 hover:text-white focus:bg-white/5 focus:text-white")}>{item_1.label}</TransitionLink>}</li>);
    $[26] = isCompact;
    $[27] = isItemActive;
    $[28] = openPopover;
    $[29] = resolveHref;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  let t20;
  if ($[31] !== t19) {
    t20 = <nav aria-label={"Navega\xE7\xE3o principal"} className="hidden items-center justify-between gap-8 lg:flex"><ul className="flex items-center gap-6">{t19}</ul></nav>;
    $[31] = t19;
    $[32] = t20;
  } else {
    t20 = $[32];
  }
  const t21 = ` px-6 rounded-2xl border border-primary-300 transition-all duration-500 ease-in-out ${isCompact ? "bg-primary-300 h-12" : "bg-transparent h-13"}`;
  const t22 = isCompact ? "bg-gray-1100" : "bg-primary-300";
  let t23;
  if ($[33] !== navigate || $[34] !== pathname) {
    t23 = event => {
      if (pathname === flowFoundationPath && scrollToSection(flowFoundationFormId)) {
        return;
      }
      navigate("/solicitar-proposta", {
        origin: {
          x: event.clientX,
          y: event.clientY
        }
      });
    };
    $[33] = navigate;
    $[34] = pathname;
    $[35] = t23;
  } else {
    t23 = $[35];
  }
  let t24;
  if ($[36] !== t21 || $[37] !== t22 || $[38] !== t23) {
    t24 = <div className="hidden lg:block"><ButtonRadialFill className={t21} classNameFill={t22} onClick={t23}>Solicitar orçamento</ButtonRadialFill></div>;
    $[36] = t21;
    $[37] = t22;
    $[38] = t23;
    $[39] = t24;
  } else {
    t24 = $[39];
  }
  let t25;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <DrawerTrigger asChild={true}><button aria-label="Abrir menu" className="flex items-center justify-center lg:hidden cursor-pointer" type="button"><AlignJustify className="size-7" strokeWidth={1.75} /></button></DrawerTrigger>;
    $[40] = t25;
  } else {
    t25 = $[40];
  }
  let t26;
  if ($[41] !== navigateToSection) {
    t26 = () => navigateToSection("/#home");
    $[41] = navigateToSection;
    $[42] = t26;
  } else {
    t26 = $[42];
  }
  let t27;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <Image src={Logo} alt="" aria-hidden="true" className="h-auto w-full max-w-32" loading="eager" />;
    $[43] = t27;
  } else {
    t27 = $[43];
  }
  let t28;
  if ($[44] !== t26) {
    t28 = <button aria-label="Flowlab Tech" onClick={t26} type="button">{t27}</button>;
    $[44] = t26;
    $[45] = t28;
  } else {
    t28 = $[45];
  }
  let t29;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <DrawerClose asChild={true}><button aria-label="Fechar menu" className="cursor-pointer flex items-center justify-center rounded-full border border-white/10 p-2.5 text-white/80 transition hover:bg-white/5 hover:text-white" type="button"><X className="size-5" strokeWidth={1.75} /></button></DrawerClose>;
    $[46] = t29;
  } else {
    t29 = $[46];
  }
  let t30;
  if ($[47] !== t28) {
    t30 = <div className="flex items-center justify-between gap-4">{t28}{t29}</div>;
    $[47] = t28;
    $[48] = t30;
  } else {
    t30 = $[48];
  }
  let t31;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <DrawerTitle className="sr-only">Menu principal</DrawerTitle>;
    $[49] = t31;
  } else {
    t31 = $[49];
  }
  let t32;
  if ($[50] !== t30) {
    t32 = <DrawerHeader className="border-b border-white/10 px-5 pt-5 pb-4">{t30}{t31}</DrawerHeader>;
    $[50] = t30;
    $[51] = t32;
  } else {
    t32 = $[51];
  }
  let t33;
  if ($[52] !== isHrefActive || $[53] !== isItemActive || $[54] !== navigateToSection || $[55] !== openMobileGroups) {
    t33 = navItems.map((item_2, index) => item_2.groups ? <div className={cn("py-2", index > 0 && "border-t border-white/10")} key={item_2.label}><p className={cn("flex w-full items-center py-3 font-bricolage text-lg", isItemActive(item_2) ? "text-primary-300" : "text-white/90")}>{item_2.label}</p><div className="mb-2 flex flex-col gap-3">{item_2.groups.map(group_0 => {
          const isGroupOpen = openMobileGroups.includes(group_0.label);
          return <div className="rounded-2xl border border-white/8 bg-white/3 px-4" key={group_0.label}><button aria-expanded={isGroupOpen} className="cursor-pointer flex w-full items-center justify-between gap-4 py-4 text-left" onClick={() => toggleMobileGroup(group_0.label)} type="button"><span className="tag mb-0! text-white">{group_0.label}</span><ChevronDown className={cn("size-4 shrink-0 text-white/50 transition-transform duration-300", isGroupOpen && "rotate-180")} /></button>{isGroupOpen && <div className="flex flex-col gap-1 border-t border-white/8 py-2">{group_0.items.map(child_0 => <button key={child_0.href} className="cursor-pointer flex items-start gap-3 py-2 text-left transition-colors" onClick={() => navigateToSection(child_0.href)} type="button"><span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-gray-1000"><Image src={child_0.icon} alt="" aria-hidden="true" className="size-7 object-contain" /></span><span className="min-w-0"><span className={cn("block font-bricolage text-sm font-medium leading-[120%] transition-colors", isHrefActive(child_0.href) ? "text-primary-300" : "text-white")}>{child_0.label}</span><span className="mt-1 block text-xs leading-[140%] text-white/60">{child_0.description}</span></span></button>)}</div>}</div>;
        })}</div></div> : <button className={cn("cursor-pointer flex w-full items-center py-4.5 text-left font-bricolage text-lg transition-colors", index > 0 && "border-t border-white/10", isItemActive(item_2) ? "text-primary-300" : "text-white/90")} key={item_2.label} onClick={() => navigateToSection(item_2.href)} type="button">{item_2.label}</button>);
    $[52] = isHrefActive;
    $[53] = isItemActive;
    $[54] = navigateToSection;
    $[55] = openMobileGroups;
    $[56] = t33;
  } else {
    t33 = $[56];
  }
  let t34;
  if ($[57] !== t33) {
    t34 = <div className="flex flex-1 flex-col overflow-y-auto px-5 py-2"><nav aria-label={"Navega\xE7\xE3o mobile"} className="flex flex-col">{t33}</nav></div>;
    $[57] = t33;
    $[58] = t34;
  } else {
    t34 = $[58];
  }
  let t35;
  if ($[59] !== navigateToSection || $[60] !== pathname) {
    t35 = <div className="border-t border-white/10 px-5 pt-4 pb-6"><ButtonRadialFill className="h-13 w-full px-6 rounded-2xl bg-primary-300 border border-primary-300" classNameFill="bg-gray-1100" onClick={() => {
        if (pathname === flowFoundationPath) {
          setIsDrawerOpen(false);
          window.setTimeout(_temp, 400);
          return;
        }
        navigateToSection("/solicitar-proposta");
      }}>Solicitar orçamento</ButtonRadialFill></div>;
    $[59] = navigateToSection;
    $[60] = pathname;
    $[61] = t35;
  } else {
    t35 = $[61];
  }
  let t36;
  if ($[62] !== t32 || $[63] !== t34 || $[64] !== t35) {
    t36 = <DrawerContent className="border-white/10 bg-gray-1100 text-white data-[vaul-drawer-direction=right]:w-full data-[vaul-drawer-direction=right]:max-w-none data-[vaul-drawer-direction=right]:sm:max-w-none">{t32}{t34}{t35}</DrawerContent>;
    $[62] = t32;
    $[63] = t34;
    $[64] = t35;
    $[65] = t36;
  } else {
    t36 = $[65];
  }
  let t37;
  if ($[66] !== isDrawerOpen || $[67] !== t36) {
    t37 = <Drawer direction="right" open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>{t25}{t36}</Drawer>;
    $[66] = isDrawerOpen;
    $[67] = t36;
    $[68] = t37;
  } else {
    t37 = $[68];
  }
  let t38;
  if ($[69] !== t15 || $[70] !== t18 || $[71] !== t20 || $[72] !== t24 || $[73] !== t37) {
    t38 = <div className={t15}>{t18}{t20}{t24}{t37}</div>;
    $[69] = t15;
    $[70] = t18;
    $[71] = t20;
    $[72] = t24;
    $[73] = t37;
    $[74] = t38;
  } else {
    t38 = $[74];
  }
  let t39;
  if ($[75] !== t13 || $[76] !== t38) {
    t39 = <div className={t13}>{t38}</div>;
    $[75] = t13;
    $[76] = t38;
    $[77] = t39;
  } else {
    t39 = $[77];
  }
  let t40;
  if ($[78] !== t11 || $[79] !== t39) {
    t40 = <header className={t11}>{t39}</header>;
    $[78] = t11;
    $[79] = t39;
    $[80] = t40;
  } else {
    t40 = $[80];
  }
  return t40;
}
function _temp() {
  return scrollToSection(flowFoundationFormId);
}